/**
 * useNewBuildDetector — detects when a new app version has been deployed
 * while the user's tab is still open, and prompts them to reload.
 *
 * Mechanism:
 *  - Vite emits /version.json at build time containing the BUILD_ID.
 *  - This hook polls /version.json on tab focus and every 5 minutes.
 *  - If the polled buildId differs from the bundled __BUILD_ID__, we know
 *    the user is on a stale bundle.
 *  - On detection, fires a react-hot-toast with a Reload button. We don't
 *    auto-reload — that would discard unsaved form state. The user clicks
 *    when they're at a good stopping point.
 *
 * Once a new version is detected, polling stops — no need to keep checking.
 */
import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import React from 'react';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 min
const VERSION_URL = '/version.json';

interface VersionPayload {
  buildId: string;
  builtAt?: string;
}

async function fetchCurrentBuildId(): Promise<string | null> {
  try {
    // cache: 'no-store' is critical — we want the *latest* version, not a
    // CDN-cached copy. Vercel respects this and forwards to origin.
    const res = await fetch(VERSION_URL, { cache: 'no-store' });
    if (!res.ok) return null;
    const ct = res.headers.get('content-type') ?? '';
    if (!ct.includes('json')) return null; // host returned a 404 HTML page
    const data: VersionPayload = await res.json();
    return data.buildId ?? null;
  } catch {
    return null;
  }
}

export function useNewBuildDetector(): void {
  // Read the bundled build ID once. `__BUILD_ID__` is a Vite define replacement
  // baked into the JS bundle at build time — it's literally a string constant
  // in the served code, so it never changes for the life of this tab.
  const bundledBuildId = (typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev');
  const notifiedRef = useRef(false);

  useEffect(() => {
    // Skip in dev — there's no /version.json and the build ID is 'dev'.
    if (bundledBuildId === 'dev') return;

    let cancelled = false;

    const check = async () => {
      if (cancelled || notifiedRef.current) return;
      const liveBuildId = await fetchCurrentBuildId();
      if (cancelled || notifiedRef.current) return;
      if (!liveBuildId) return;
      if (liveBuildId === bundledBuildId) return;

      // Mismatch — user is on a stale bundle.
      notifiedRef.current = true;
      toast(
        (t) =>
          React.createElement(
            'div',
            { className: 'flex items-center gap-3' },
            React.createElement(
              'div',
              { className: 'flex flex-col' },
              React.createElement(
                'span',
                /* Sin `font-display`: Archivo queda para la landing y los
                   documentos impresos. En una etiqueta de interfaz delata el
                   "hecho con plantilla" que el rediseño vino a quitar. */
                { className: 'font-semibold text-sm text-white' },
                'New version available',
              ),
              React.createElement(
                'span',
                { className: 'text-xs text-acero-400' },
                'Reload to get the latest features and fixes.',
              ),
            ),
            React.createElement(
              'button',
              {
                onClick: () => window.location.reload(),
                className: 'presionable ml-2 h-8 px-3 bg-brand-400 text-navy-950 text-xs font-semibold rounded-xl hover:bg-brand-300 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-850',
              },
              'Reload',
            ),
            React.createElement(
              'button',
              {
                onClick: () => toast.dismiss(t.id),
                className: 'p-1 rounded-lg text-acero-400 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
                'aria-label': 'Dismiss',
              },
              '✕',
            ),
          ),
        { duration: Infinity, position: 'bottom-right' },
      );
    };

    // Initial check after a short delay (don't slam the network during boot).
    const initialTimeout = setTimeout(check, 30_000);

    // Periodic polling while tab is open.
    const interval = setInterval(check, POLL_INTERVAL_MS);

    // Tab focus check — covers the long-tail of users who background a tab
    // overnight, deploy happens at 3am, they come back at 9am.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearTimeout(initialTimeout);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [bundledBuildId]);
}
